/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/hooks/useCategoryQuery.ts
 * @Description: 商品分类数据查询 Hook
 *
 * 使用 React Query 在客户端获取商品分类数据
 * 替代原有的服务端渲染方式，提升首屏加载性能
 */

import { useQuery } from '@tanstack/react-query';
import { getCategory } from 'api/user';
import { queryKeys } from 'libs/react-query';

/**
 * 商品分类数据的类型定义
 */
export interface Category {
  id: string | number;
  title: string;
  link: string;
  children?: Category[];
  cascadeId?: string;
  [key: string]: any;
}

/**
 * 使用 React Query 获取商品分类数据
 *
 * @param options - 查询选项
 * @param options.enabled - 是否启用查询（默认为 true）
 * @param options.initialData - 初始数据（可选，用于服务端渲染场景）
 *
 * @returns 返回查询结果对象，包含：
 *   - data: 商品分类数据数组
 *   - isLoading: 是否正在首次加载
 *   - isFetching: 是否正在获取数据（包括后台更新）
 *   - isError: 是否发生错误
 *   - error: 错误信息
 *   - refetch: 手动重新获取数据的函数
 *
 * @example
 * ```tsx
 * function CategoryNav() {
 *   const { data: categories, isLoading, isError } = useCategoryQuery();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (isError) return <div>加载失败</div>;
 *
 *   return (
 *     <nav>
 *       {categories?.map(category => (
 *         <a key={category.id} href={category.link}>
 *           {category.title}
 *         </a>
 *       ))}
 *     </nav>
 *   );
 * }
 * ```
 */
export function useCategoryQuery() {
  return useQuery({
    // 查询键，用于缓存和标识查询
    queryKey: queryKeys.category.list(),

    // 查询函数，返回 Promise
    queryFn: async () => {
      try {
        const res = await getCategory({
          query: { _t: Date.now() },
        });

        if (res.code === 200) {
          return res.data?.categories || [];
        }

        throw new Error(res.message || '获取商品分类失败');
      } catch (error) {
        console.error('获取商品分类数据失败:', error);
        throw error;
      }
    },
  });
}

/**
 * 预加载商品分类数据
 * 用于在需要数据之前提前加载，提升用户体验
 *
 * @param queryClient - React Query 的 QueryClient 实例
 *
 * @example
 * ```tsx
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchCategories } from 'hooks/useCategoryQuery';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // 在应用启动时预加载分类数据
 *     prefetchCategories(queryClient);
 *   }, []);
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export async function prefetchCategories(queryClient: any) {
  await queryClient.prefetchQuery({
    queryKey: queryKeys.category.list(),
    queryFn: async () => {
      const res = await getCategory({
        query: { _t: Date.now() },
      });
      if (res.code === 200) {
        return res.data?.categories || [];
      }
      throw new Error(res.message || '获取商品分类失败');
    },
    staleTime: 10 * 60 * 1000, // 10分钟
  });
}
